import { HttpClient, HttpContext } from '@angular/common/http';
import { DOCUMENT, DestroyRef, Injectable, Injector, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { type Observable, catchError, defer, filter, firstValueFrom, map, of } from 'rxjs';
import { environment } from '@environment';
import { isWindowPane, overlayContainerElement } from '@lite-workspace/shared/helm-ui/utils';
import { IdentityStore } from '@lite-workspace/shared/identity';
import { AnalyticsService, SKIP_ERROR_TRACKING_STATUSES } from '@lite-workspace/shared/services/analytics';
import { MOBILE_QUERY } from '@lite-workspace/utils';

/** Chat place: never, over the editor bottom bar, the book editor's corner, beside the design editor's zoom, or default. */
export type SupportWidgetZone = 'hidden' | 'editor' | 'book-editor' | 'design-editor' | 'default';

type ShownZone = Exclude<SupportWidgetZone, 'hidden'>;

type SupportWidgetTheme = 'light' | 'dark';

interface Position {
  right: number;
  bottom: number;
  size: number;
  /** Closed button drawn translucent in every zone; full colour under the pointer. */
  dim?: boolean;
}

/** Bottom-right corner of the open window; unset, the window covers the launcher slot. */
interface OpenAnchor {
  right: number;
  bottom: number;
}

export interface SupportWidgetOpenOptions {
  message?: string;
}

/** The loader's public API (`/widget/v1/loader.js`). */
interface LumirSupportApi {
  boot(options: {
    siteKey: string;
    identityToken: string;
    context?: { openpanelSessionId?: string };
    refreshIdentityToken?: () => Promise<string>;
    launcher?: boolean;
    theme?: SupportWidgetTheme;
  }): void;
  shutdown(): void;
  /** `message` = first line the photographer sends in the opened chat. */
  open(options?: SupportWidgetOpenOptions): void;
  setPosition(p: Position, open?: OpenAnchor): void;
  hide(): void;
  show(): void;
  onUnread(cb: (count: number) => void): void;
  setTheme(theme: SupportWidgetTheme): void;
}

type LumirSupportStub = { q: [string, ArrayLike<unknown>][] };
type SupportWindow = Window & { LumirSupport?: LumirSupportApi | LumirSupportStub };

const SUPPORT_WIDGET_SITE_KEY = 'lumir';
export const SUPPORT_WIDGET_IDENTITY_ENDPOINT = '/api/support/widget-identity';
// The token lives an hour: a fresh one lands in the widget before the old one dies.
const SUPPORT_WIDGET_REFRESH_LEAD_MS = 5 * 60_000;
// A token already near its end must not spin the refresh in a tight loop.
const MIN_REFRESH_DELAY_MS = 60_000;
// 401 — no session, 404 — widget off, 409 — user has no email: the widget stays off and error tracking
// skips them; a 401 still passes the app's global error interceptor.
const EXPECTED_FAILURE_STATUSES: readonly number[] = [401, 404, 409];

/** Button spot from the canvas's bottom-right corner, while an editor canvas publishes its insets. */
interface CanvasSpot {
  right: number;
  gap: number;
}

// Album and book editors: window corner, not the canvas. The button stays put; the window opens 188 + bars up.
const EDITOR_CORNER = { launcher: { right: 16, bottom: 16, size: 44, dim: true }, open: { right: 24, bottom: 188 } };
const EDITOR_ZONES: ReadonlySet<SupportWidgetZone> = new Set(['editor', 'book-editor']);

// The zoom control sits 16px above the canvas bottom, 34px tall. `launcher` = the spot without a canvas.
const PLACES: Record<ShownZone, { launcher: Position; open?: OpenAnchor; canvas?: CanvasSpot }> = {
  default: { launcher: { right: 24, bottom: 24, size: 56, dim: true } },
  editor: EDITOR_CORNER,
  'book-editor': EDITOR_CORNER,
  // Left of the zoom control, centred on it: 16 + 34 / 2 - 44 / 2. The window opens 188 + bars up, not in the button slot.
  'design-editor': {
    launcher: { right: 142, bottom: 137, size: 44, dim: true },
    open: { right: 24, bottom: 188 },
    canvas: { right: 142, gap: 11 },
  },
};
// A canvas reaching the editor's corner puts its zoom control (16px in, 34 × ~118) under the button: 16 + 34, 16 + 118.
const ZOOM_CLEAR_INSET_B = 50;
const ZOOM_CLEAR_INSET_R = 134;
// 12px above that zoom control: 16 + 34 + 12.
const ABOVE_ZOOM_BOTTOM = 62;
const CANVAS_INSET_BOTTOM_VAR = '--editor-canvas-inset-b';
// Mouse on a desktop-wide window gets the floating button; touch and phone layouts open the chat from the menu.
const FINE_POINTER_QUERY = '(pointer: fine)';
// Room the default-zone button takes above the toast baseline (ds-theme `--bottom-toast`).
const LAUNCHER_HEIGHT_VAR = '--support-launcher-h';

function tokenExpiresAt(token: string): number | undefined {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const exp: unknown = JSON.parse(atob(payload)).exp;
    return typeof exp === 'number' ? exp * 1000 : undefined;
  } catch {
    return undefined;
  }
}

let stopActiveWidget: (() => void) | null = null;

/** Logout paths outside an injection context (toast actions, plain functions) stop the widget here. */
export function stopSupportWidget(): void {
  stopActiveWidget?.();
}

@Injectable({ providedIn: 'root' })
export class SupportWidgetService {
  // HttpClient and analytics are taken on use: hosts without them (showcase, specs) still read `running`.
  private readonly injector = inject(Injector);
  private readonly document = inject(DOCUMENT);
  private readonly origin: string = environment.SUPPORT_WIDGET_ORIGIN;
  private readonly enabled: boolean = environment.supportWidgetEnabled;

  private readonly runningState = signal(false);
  /** The widget is booted: a chat row may offer it. */
  readonly running = this.runningState.asReadonly();
  private readonly unreadState = signal(0);
  /** Operator replies the photographer has not read yet. */
  readonly unread = this.unreadState.asReadonly();

  // Generation of the start in flight: a start left over from before stop() does not block a new one.
  private startingGen: number | null = null;
  private scriptInserted = false;
  private refreshTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly zoneState = signal<SupportWidgetZone>('default');
  readonly zone = this.zoneState.asReadonly();
  // Bumped by stop(): a token that arrives after a logout boots nothing.
  private generation = 0;
  private rootObserver: MutationObserver | undefined;
  private sentTheme: SupportWidgetTheme | undefined;
  private launcher = false;
  private launcherQueries: MediaQueryList[] = [];
  private sentPosition: string | undefined;
  // A token asked on a hidden page: the widget re-boots once a page shows it again.
  private rebootOwed = false;
  // A loading veil over the page: the button waits under it, the page's zone kept.
  private veiled = false;
  private readonly overlayRoot = overlayContainerElement();
  private overlayObserver: MutationObserver | undefined;
  // The loader's iframe sits above the CDK layer: an open menu or dialog would lie under the button until it closes.
  private overlayOpen = false;
  // A chat asked for from a menu or dialog stays shown until those windows are gone or a new one opens.
  private overlayLifted = false;
  private liftedOver: Element[] = [];

  constructor() {
    stopActiveWidget = (): void => this.stop();
    inject(DestroyRef).onDestroy((): void => {
      this.rootObserver?.disconnect();
      this.overlayObserver?.disconnect();
    });
  }

  /** Boots the widget once per signed-in session; a gateway refusal leaves it off. */
  async start(): Promise<void> {
    const gen = this.generation;
    if (!this.enabled || !this.origin || this.startingGen === gen || this.runningState()) return;
    this.startingGen = gen;
    try {
      const identityToken = await this.fetchToken();
      if (!identityToken || gen !== this.generation) return;
      this.insertScript();
      this.watchLauncher();
      this.boot(identityToken);
      // Shutdown drops loader listeners: every start subscribes again.
      this.call('onUnread', (count: number): void => this.unreadState.set(count));
      this.runningState.set(true);
      this.applyZone();
      this.followRoot();
      this.followOverlays();
    } finally {
      if (this.startingGen === gen) this.startingGen = null;
    }
  }

  /** Logout: the widget closes its session and leaves the page. */
  stop(): void {
    this.generation += 1;
    this.rebootOwed = false;
    this.releaseHost();
    if (!this.runningState()) return;
    this.runningState.set(false);
    this.unreadState.set(0);
    this.call('shutdown');
  }

  // Timers, watchers and the toasts' room the widget holds on the page: the next start sends its spot again.
  private releaseHost(): void {
    clearTimeout(this.refreshTimer);
    this.refreshTimer = undefined;
    this.rootObserver?.disconnect();
    this.rootObserver = undefined;
    this.overlayObserver?.disconnect();
    this.overlayObserver = undefined;
    this.overlayOpen = false;
    this.overlayLifted = false;
    this.liftedOver = [];
    for (const query of this.launcherQueries) query.removeEventListener('change', this.onLauncherQuery);
    this.launcherQueries = [];
    this.sentPosition = undefined;
    this.document.documentElement.style.removeProperty(LAUNCHER_HEIGHT_VAR);
  }

  /** Opens the chat (queued while the loader is on its way); false when it cannot — the caller offers its fallback. */
  open(options?: SupportWidgetOpenOptions): boolean {
    if (!this.runningState() || this.pageZone() === 'hidden') return false;
    // Menu rows and dialogs ask for the chat while their overlay is still in the DOM.
    if (this.overlayOpen && !this.overlayLifted) {
      this.overlayLifted = true;
      this.liftedOver = this.windowPanes();
      this.applyZone();
    }

    if (options) this.call('open', options);
    else this.call('open');
    return true;
  }

  setZone(zone: SupportWidgetZone): void {
    const from = this.zoneState();
    if (zone === from) return;
    // The editor shell mounts its veil after the route lands: the button waits from zone entry until the shell lifts it.
    if (EDITOR_ZONES.has(zone) !== EDITOR_ZONES.has(from)) this.veiled = EDITOR_ZONES.has(zone);
    this.zoneState.set(zone);
    if (this.runningState()) this.applyZone();
  }

  /** A loading veil covers the page: the button hides under it and returns to the page's zone once it lifts. */
  setVeil(shown: boolean): void {
    if (shown === this.veiled) return;
    this.veiled = shown;
    if (this.runningState()) this.applyZone();
  }

  private pageZone(): SupportWidgetZone {
    return this.veiled ? 'hidden' : this.zoneState();
  }

  private shownZone(): SupportWidgetZone {
    return this.overlayOpen && !this.overlayLifted ? 'hidden' : this.pageZone();
  }

  private applyZone(): void {
    const zone = this.shownZone();
    if (zone === 'hidden') {
      this.sentPosition = undefined;
      // An open window only covers the button: the toasts keep its room and do not jump on every menu.
      if (this.pageZone() === 'hidden' || !this.launcher) this.document.documentElement.style.removeProperty(LAUNCHER_HEIGHT_VAR);
      this.call('hide');
      return;
    }

    this.place(zone);
    this.call('show');
    if (this.rebootOwed) void this.rebootWithFreshToken();
  }

  // Bars publish their height and an editor canvas its bottom and right gaps on <html>: the widget stands clear of them.
  private place(zone: ShownZone): void {
    const { launcher: base, open, canvas } = PLACES[zone];
    const uploadBar = this.rootPx('--upload-bar-h');
    const lift = this.rootPx('--wz-tray-h') + uploadBar;
    // Present at 0px too: the canvas then reaches the window bottom (strip folded).
    const insetB = this.document.documentElement.style.getPropertyValue(CANVAS_INSET_BOTTOM_VAR);
    const spot = canvas && insetB ? canvas : undefined;
    const shift = insetB ? this.rootPx('--editor-canvas-inset-r') : 0;
    const underZoom =
      PLACES[zone] === EDITOR_CORNER && !!insetB && (parseFloat(insetB) || 0) < ZOOM_CLEAR_INSET_B && shift < ZOOM_CLEAR_INSET_R;
    const position: Position = spot
      ? { size: base.size, dim: base.dim, right: spot.right + shift, bottom: (parseFloat(insetB) || 0) + spot.gap + lift }
      : { ...base, bottom: (underZoom ? ABOVE_ZOOM_BOTTOM : base.bottom) + lift };
    // The shift moves the window even when the button stays put.
    const key = `${zone}:${position.right}:${position.bottom}:${shift}`;
    // Publishing the variable below mutates <html> again: an unchanged spot stops the loop.
    if (key === this.sentPosition) return;
    this.sentPosition = key;
    if (open) this.call('setPosition', position, { right: open.right + shift, bottom: open.bottom + lift });
    else this.call('setPosition', position);

    const style = this.document.documentElement.style;
    // The toast baseline already counts the tray.
    if (this.launcher && zone === 'default') {
      style.setProperty(LAUNCHER_HEIGHT_VAR, `calc(${uploadBar + base.size}px + var(--spacing) * 3)`);
    } else {
      style.removeProperty(LAUNCHER_HEIGHT_VAR);
    }
  }

  private rootPx(name: string): number {
    return parseFloat(this.document.documentElement.style.getPropertyValue(name)) || 0;
  }

  // A hidden page holds a client's bearer: no identity request leaves it, whoever asks.
  private fetchToken(): Promise<string | undefined> {
    this.rebootOwed = this.zoneState() === 'hidden';
    if (this.rebootOwed) return Promise.resolve(undefined);
    const request = (): Observable<string | undefined> =>
      this.injector
        .get(HttpClient)
        .get<{ token?: string }>(SUPPORT_WIDGET_IDENTITY_ENDPOINT, {
          context: new HttpContext().set(SKIP_ERROR_TRACKING_STATUSES, EXPECTED_FAILURE_STATUSES),
        })
        .pipe(map(response => (typeof response?.token === 'string' && response.token ? response.token : undefined)));
    // Errors end inside the stream: a rejected promise awaited later reads as unhandled to zone.js.
    return firstValueFrom(defer(request).pipe(catchError(() => of(undefined))));
  }

  // Asked by the widget when its token expired: a refusal rejects, and the widget turns off.
  private async refreshToken(): Promise<string> {
    const gen = this.generation;
    const token = await this.fetchToken();
    if (!token || gen !== this.generation) throw new Error('support widget identity unavailable');
    this.scheduleRefresh(token);
    return token;
  }

  private scheduleRefresh(token: string): void {
    clearTimeout(this.refreshTimer);
    const expiresAt = tokenExpiresAt(token);
    if (expiresAt === undefined) return;
    const delay = Math.max(expiresAt - Date.now() - SUPPORT_WIDGET_REFRESH_LEAD_MS, MIN_REFRESH_DELAY_MS);
    this.refreshTimer = setTimeout((): void => {
      void this.rebootWithFreshToken();
    }, delay);
  }

  // Same user, same window: the widget only keeps the fresh token for its next session.
  private async rebootWithFreshToken(): Promise<void> {
    const gen = this.generation;
    const identityToken = await this.fetchToken();
    if (!identityToken || gen !== this.generation) return;
    // The page hid while the token was on its way: the button must not come back over it.
    if (this.shownZone() === 'hidden') {
      this.rebootOwed = true;
      return;
    }

    this.boot(identityToken);
  }

  private boot(identityToken: string): void {
    const launcher = this.launcherWanted();
    const flipped = launcher !== this.launcher;
    this.launcher = launcher;
    const sessionId = this.injector.get(AnalyticsService, null)?.getSessionId();
    this.call('boot', {
      siteKey: SUPPORT_WIDGET_SITE_KEY,
      identityToken,
      context: sessionId ? { openpanelSessionId: sessionId } : {},
      refreshIdentityToken: (): Promise<string> => this.refreshToken(),
      launcher: this.launcher,
      theme: this.hostTheme(),
    });
    this.scheduleRefresh(identityToken);
    // The button came or went: its spot and the toasts' room are sent again.
    if (flipped && this.runningState()) {
      this.sentPosition = undefined;
      this.applyZone();
    }
  }

  // A window resized across the phone layout or a pointer swap re-boots the widget with the other launcher.
  private watchLauncher(): void {
    const win = this.document.defaultView;
    if (!win?.matchMedia || this.launcherQueries.length) return;
    this.launcherQueries = [win.matchMedia(FINE_POINTER_QUERY), win.matchMedia(MOBILE_QUERY)];
    for (const query of this.launcherQueries) query.addEventListener('change', this.onLauncherQuery);
  }

  private readonly onLauncherQuery = (): void => {
    if (this.runningState() && this.launcherWanted() !== this.launcher) void this.rebootWithFreshToken();
  };

  private launcherWanted(): boolean {
    const [fine, narrow] = this.launcherQueries;
    return !!fine?.matches && !narrow?.matches;
  }

  // Every app marks its theme with `dark` on <html>; gallery keeps its own theme service, so the class is the one source.
  private hostTheme(): SupportWidgetTheme {
    return this.document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  }

  // <html> carries the theme class and the bar heights the button stands on.
  private followRoot(): void {
    const Observer = this.document.defaultView?.MutationObserver;
    if (!Observer || this.rootObserver) return;
    this.sentTheme = this.hostTheme();
    this.rootObserver = new Observer((): void => {
      const zone = this.shownZone();
      if (zone !== 'hidden') this.place(zone);
      const theme = this.hostTheme();
      if (theme === this.sentTheme) return;
      this.sentTheme = theme;
      if (!this.loaderLacks('setTheme')) this.call('setTheme', theme);
    });
    this.rootObserver.observe(this.document.documentElement, { attributes: true, attributeFilter: ['class', 'style'] });
  }

  private followOverlays(): void {
    const Observer = this.document.defaultView?.MutationObserver;
    if (!Observer || this.overlayObserver) return;
    this.overlayObserver = new Observer((): void => this.trackOverlays());
    this.overlayObserver.observe(this.overlayRoot, { childList: true });
    this.trackOverlays();
  }

  private windowPanes(): Element[] {
    return Array.from(this.overlayRoot.querySelectorAll('.cdk-overlay-pane')).filter(isWindowPane);
  }

  private trackOverlays(): void {
    const panes = this.windowPanes();
    const open = panes.length > 0;
    // CDK re-attaches the same pane when a menu reopens: a pane that left and came back counts as new.
    this.liftedOver = this.liftedOver.filter(pane => panes.includes(pane));
    // The lift holds over the windows the chat was asked from; a window opened after it covers the button again.
    const lifted = this.overlayLifted && open && panes.every(pane => this.liftedOver.includes(pane));
    if (!lifted) this.liftedOver = [];
    if (open === this.overlayOpen && lifted === this.overlayLifted) return;
    this.overlayOpen = open;
    this.overlayLifted = lifted;
    if (this.runningState()) this.applyZone();
  }

  private insertScript(): void {
    if (this.scriptInserted) return;
    this.scriptInserted = true;
    const script = this.document.createElement('script');
    script.async = true;
    script.src = `${this.origin}/widget/v1/loader.js`;
    // Loader blocked or down: the widget is off, every caller offers its fallback.
    script.onerror = (): void => {
      this.scriptInserted = false;
      this.releaseHost();
      const win = this.document.defaultView as SupportWindow | null;
      if (win?.LumirSupport && 'q' in win.LumirSupport) delete win.LumirSupport;
      this.runningState.set(false);
      this.unreadState.set(0);
    };

    this.document.head.appendChild(script);
  }

  // A loader cached from an older release (rollout skew) lacks the newer calls: setTheme.
  // The stub queue counts as current: the loader that replays it is the one just requested.
  private loaderLacks(name: keyof LumirSupportApi): boolean {
    const current = (this.document.defaultView as SupportWindow | null)?.LumirSupport;
    return !!current && !('q' in current) && typeof current[name] !== 'function';
  }

  // Before the loader arrives, calls wait in the stub queue it replays on load.
  private call<K extends keyof LumirSupportApi>(name: K, ...args: Parameters<LumirSupportApi[K]>): void {
    const win = this.document.defaultView as SupportWindow | null;
    if (!win) return;
    const current = (win.LumirSupport ??= { q: [] });
    if ('q' in current) {
      current.q.push([name, args]);
      return;
    }

    (current[name] as (...a: unknown[]) => void)(...args);
  }
}

/**
 * Cabinet wiring (an app initializer): boots the widget once the profile is loaded on a page where it shows,
 * and moves it per page. `zoneOf` maps the page path (router url form) to the widget's place there.
 */
export function connectSupportWidget(zoneOf: (url: string) => SupportWidgetZone): void {
  const widget = inject(SupportWidgetService);
  const identity = inject(IdentityStore);
  const router = inject(Router);
  const { pathname, search } = inject(DOCUMENT).location;

  // Initializers run before the first navigation: the address bar already names the page.
  widget.setZone(zoneOf(`${pathname}${search}`));
  router.events
    .pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed(inject(DestroyRef)),
    )
    .subscribe((): void => widget.setZone(zoneOf(router.url)));

  // A hidden page never boots it: a student's bearer must not reach the identity endpoint.
  effect((): void => {
    if (!identity.profile() || widget.zone() === 'hidden') return;
    untracked((): void => {
      void widget.start();
    });
  });
}
